import Cookies from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, Logger, sendPackageOperationalEvent } from '../../../common/utils';
import { getItemCategoryFromPassedCache } from '../../../common/utils/item-processing';
import { findStorageTarget, WebStorageType } from '../../../services/web-storage-service';
import {
	CategoryStorageType,
	PreferenceCategory,
	type StorageCleanupItem,
	type StorageCleanupResult,
	type UncategorizedStorageItem,
} from '../../../types';
import { getPreferences } from '../../preferences-controls/get-preferences';
import { deleteCookie } from '../delete-cookie';

function isCategoryRevoked({
	category,
	preferences,
}: {
	category: PreferenceCategory;
	preferences: Awaited<ReturnType<typeof getPreferences>>;
}): boolean {
	if (category === PreferenceCategory.StrictlyNecessary) {
		return false;
	}
	if (!preferences) {
		return false;
	}
	return !preferences[category];
}

function yieldToMain(): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, 0));
}

const ITEMS_PER_YIELD = 50;
const ITEM_CAP = 500;

export async function cleanupStorage(): Promise<StorageCleanupResult> {
	try {
		const cleanupStartTime = performance.now();
		const globalState = getBscGlobalState();
		const preferences = await getPreferences();
		const localStorage = findStorageTarget(WebStorageType.LocalStorage);
		const sessionStorage = findStorageTarget(WebStorageType.SessionStorage);
		const idb = window.indexedDB;
		const productName = globalState.product;

		const [localStorageCache, sessionStorageCache, cookieCache, indexedDBCache] = await Promise.all(
			[
				globalState.loadCache(CategoryStorageType.LocalStorage),
				globalState.loadCache(CategoryStorageType.SessionStorage),
				globalState.loadCache(CategoryStorageType.Cookies),
				globalState.loadCache(CategoryStorageType.IndexedDB),
			],
		);

		const revokedItems: StorageCleanupItem[] = [];
		const uncategorizedItems: UncategorizedStorageItem[] = [];
		let hitEnumerationLimit = false;
		let itemsProcessed = 0;
		let deletionCount = 0;

		// localStorage
		const shouldDeleteWebStorage =
			!globalState.disableWebStorageConsentChecks && globalState.webStorageEnabled;
		if (localStorageCache) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			for (let i = 0; i < localStorage.length(); i++) {
				if (itemsProcessed >= ITEM_CAP) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
				const key = localStorage.key(i);
				if (key === null) {
					continue;
				}
				const category = getItemCategoryFromPassedCache(key, localStorageCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'localStorage' });
				} else if (isCategoryRevoked({ category, preferences })) {
					revokedItems.push({ key, storageType: 'localStorage', category });
					if (shouldDeleteWebStorage && fg('platform_bsc_cleanup_revoked_storage_live_delete')) {
						// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
						localStorage.removeItem(key);
						i--; // removing an item shifts later ones; use same index for the next iteration
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// sessionStorage
		if (!hitEnumerationLimit && sessionStorageCache) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			for (let i = 0; i < sessionStorage.length(); i++) {
				if (itemsProcessed >= ITEM_CAP) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
				const key = sessionStorage.key(i);
				if (key === null) {
					continue;
				}
				const category = getItemCategoryFromPassedCache(key, sessionStorageCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'sessionStorage' });
				} else if (isCategoryRevoked({ category, preferences })) {
					revokedItems.push({ key, storageType: 'sessionStorage', category });
					if (shouldDeleteWebStorage && fg('platform_bsc_cleanup_revoked_storage_live_delete')) {
						// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
						sessionStorage.removeItem(key);
						i--; // removing an item shifts later ones; use same index for the next iteration
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// cookies
		if (!hitEnumerationLimit && cookieCache) {
			for (const key of Object.keys(Cookies.get())) {
				if (itemsProcessed >= ITEM_CAP) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				const category = getItemCategoryFromPassedCache(key, cookieCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'cookie' });
				} else if (isCategoryRevoked({ category, preferences })) {
					revokedItems.push({ key, storageType: 'cookie', category });
					// There's no corresponding `shouldDeleteWebStorage` for cookies as they have no reporting-only mode.
					if (fg('platform_bsc_cleanup_revoked_storage_live_delete')) {
						deleteCookie(key);
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// IndexedDB
		const shouldDeleteIdb =
			!globalState.disableIndexedDbConsentChecks && globalState.indexedDbEnabled;
		if (!hitEnumerationLimit && indexedDBCache && typeof idb?.databases === 'function') {
			try {
				const dbs = await idb.databases();
				for (const db of dbs) {
					if (itemsProcessed >= ITEM_CAP) {
						hitEnumerationLimit = true;
						break;
					}
					if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
						await yieldToMain();
					}
					const name = db.name;
					if (name === undefined) {
						continue;
					}
					const category = getItemCategoryFromPassedCache(name, indexedDBCache, productName);
					if (category === null || category === PreferenceCategory.Unknown) {
						uncategorizedItems.push({ key: name, storageType: 'indexedDB' });
					} else if (isCategoryRevoked({ category, preferences })) {
						revokedItems.push({ key: name, storageType: 'indexedDB', category });
						if (shouldDeleteIdb && fg('platform_bsc_cleanup_revoked_storage_live_delete')) {
							const request = idb.deleteDatabase(name);
							request.onerror = () => {
								Logger.warn(
									`cleanupStorage: failed to delete IndexedDB "${name}": ${request.error?.message ?? ''}`,
								);
							};
							deletionCount++;
						}
					}
					itemsProcessed++;
				}
			} catch {
				// idb.databases() unavailable or threw — skip IDB entirely
			}
		}

		if (deletionCount > 0) {
			sendPackageOperationalEvent({
				action: 'storageCleanupDeleted',
				attributes: { deletedCount: deletionCount },
			});
		}

		sendPackageOperationalEvent({
			action: 'storageCleanupCategorized',
			attributes: {
				durationMs: Math.round(performance.now() - cleanupStartTime),
				deletionMode: fg('platform_bsc_cleanup_revoked_storage_live_delete') ? 'live' : 'dry-run',
				revokedCount: revokedItems.length,
				revokedItems: revokedItems.map(
					({ storageType, key, category }) => `${storageType} ${key} ${category}`,
				),
				uncategorizedCount: uncategorizedItems.length,
				uncategorizedItems: uncategorizedItems.map(
					({ storageType, key }) => `${storageType} ${key}`,
				),
				hitEnumerationLimit,
			},
		});

		return {
			deletionMode: fg('platform_bsc_cleanup_revoked_storage_live_delete') ? 'live' : 'dry-run',
			revokedItems,
			uncategorizedItems,
			hitEnumerationLimit,
		};
	} catch (error: any) {
		Logger.errorWithOperationalEvent({
			action: 'storageCleanupError',
			message: `cleanupStorage failed: ${error?.message ?? 'no message'}`,
		});
		return {
			deletionMode: fg('platform_bsc_cleanup_revoked_storage_live_delete') ? 'live' : 'dry-run',
			revokedItems: [],
			uncategorizedItems: [],
			hitEnumerationLimit: false,
		};
	}
}
